(() => {
  "use strict";

  let updateScheduled = false;
  let reviewsBeforeLoad = 0;
  let loadMoreTriggered = false;

  /**
   * Prevents repeated observer executions during a batch
   * of Judge.me DOM updates.
   */
  function scheduleUpdate() {
    if (updateScheduled) return;

    updateScheduled = true;

    requestAnimationFrame(() => {
      updateScheduled = false;
      runJudgeMeEnhancements();
    });
  }

  /**
   * Returns only currently visible reviews.
   */
  function getVisibleReviews() {
    return [...document.querySelectorAll(".jdgm-rev")].filter((review) => {
      if (review.hidden) return false;
      if (review.getAttribute("aria-hidden") === "true") return false;

      return review.getClientRects().length > 0;
    });
  }

  /**
   * Improves the main Judge.me summary.
   */
  function enhanceSummary() {
    const summary = document.querySelector(".jdgm-all-reviews__summary");
    if (!summary) return;

    const average = summary.querySelector(
      ".jdgm-all-reviews__summary-average",
    );

    if (average && average.dataset.split !== "true") {
      const text = average.textContent.trim();
      const match = text.match(/^([\d.,]+)\s+(out of\s+\d+)/i);

      if (match) {
        const ratingValue = document.createElement("span");
        ratingValue.className = "jdgm-rating-value";
        ratingValue.textContent = match[1];

        const ratingRest = document.createElement("span");
        ratingRest.className = "jdgm-rating-rest";
        ratingRest.textContent = match[2];

        average.replaceChildren(ratingValue, ratingRest);
        average.dataset.split = "true";
      }
    }

    const starsBlock = summary.querySelector(
      ".jdgm-all-reviews__summary-stars",
    );

    if (starsBlock && !starsBlock.querySelector(".jdgm-stars-inner")) {
      const stars = [...starsBlock.querySelectorAll(".jdgm-star")];

      if (stars.length) {
        const wrapper = document.createElement("div");
        wrapper.className = "jdgm-stars-inner";

        stars.forEach((star) => wrapper.appendChild(star));
        starsBlock.appendChild(wrapper);
      }
    }

    const value = summary
      .querySelector(".jdgm-rating-value")
      ?.textContent.trim();

    const outOf = summary
      .querySelector(".jdgm-rating-rest")
      ?.textContent.trim();

    if (starsBlock && value && outOf) {
      starsBlock.setAttribute(
        "aria-label",
        `Average rating is ${value} ${outOf}`,
      );
    }
  }

  /**
   * Places tabs and sorting actions in one row.
   */
  function enhanceTabsRow() {
    const subtab = document.querySelector(".jdgm-subtab");
    const actions = document.querySelector(".jdgm-row-actions");

    if (!subtab || !actions) return;
    if (subtab.closest(".jdgm-tabs-row")) return;

    const wrapper = document.createElement("div");
    wrapper.className = "jdgm-tabs-row";

    subtab.parentNode.insertBefore(wrapper, subtab);
    wrapper.append(subtab, actions);
  }

  /**
   * Adds tab semantics and synchronizes selected states.
   */
  function enhanceTabsAccessibility() {
    const tabList = document.querySelector(".jdgm-subtab");
    if (!tabList) return;

    tabList.setAttribute("role", "tablist");

    tabList.querySelectorAll(".jdgm-subtab__name").forEach((tab) => {
      const isActive = tab.classList.contains("jdgm--active");

      tab.setAttribute("role", "tab");
      tab.setAttribute("tabindex", isActive ? "0" : "-1");
      tab.setAttribute("aria-selected", isActive ? "true" : "false");
    });
  }

  /**
   * Applies all transformations to one review.
   */
  function enhanceReview(review) {
    if (!(review instanceof HTMLElement)) return;

    removeUnusedReviewElements(review);
    enhanceReviewHeader(review);
    setReviewInitials(review);
    moveReviewFlag(review);
    moveReviewTimestamp(review);
    labelReviewPhotos(review);
  }

  function removeUnusedReviewElements(review) {
    if (review.dataset.cleaned === "true") return;

    const selectors = [
      ".jdgm-rev__title",
      ".jdgm-rev__custom-form",
      ".jdgm-rev__actions",
      ".jdgm-rev__transparency-badge-wrapper",
    ];

    selectors.forEach((selector) => {
      review.querySelector(selector)?.remove();
    });

    review.dataset.cleaned = "true";
  }

  function enhanceReviewHeader(review) {
    if (review.querySelector(".jdgm-prod-rating-row")) return;

    const product = review.querySelector(".jdgm-row-product");
    const rating = review.querySelector(".jdgm-row-rating");
    const content = review.querySelector(".jdgm-rev__content");

    if (!product || !rating || !content) return;

    const wrapper = document.createElement("div");
    wrapper.className = "jdgm-prod-rating-row";

    content.prepend(wrapper);
    wrapper.append(product, rating);
  }

  function setReviewInitials(review) {
    const icon = review.querySelector(".jdgm-rev__icon");
    const author = review.querySelector(".jdgm-rev__author");

    if (!icon || !author) return;

    const name = author.textContent.trim();
    if (!name) return;

    const initials = name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word.charAt(0).toUpperCase())
      .join("");

    if (icon.dataset.initials !== initials) {
      icon.dataset.initials = initials;
    }
  }

  function moveReviewFlag(review) {
    const author = review.querySelector(".jdgm-rev__author");
    const flag = review.querySelector(
      ".jdgm-rev__location-country-flag-img",
    );

    if (!author || !flag) return;
    if (flag.dataset.moved === "true") return;

    author.insertAdjacentElement("afterend", flag);
    flag.dataset.moved = "true";
  }

  function moveReviewTimestamp(review) {
    const profile = review.querySelector(".jdgm-row-profile");
    const timestamp = review.querySelector(".jdgm-rev__timestamp");

    if (!profile || !timestamp) return;
    if (timestamp.dataset.moved === "true") return;

    profile.appendChild(timestamp);
    timestamp.dataset.moved = "true";
  }

  function labelReviewPhotos(review) {
    const authorName =
      review.querySelector(".jdgm-rev__author")?.textContent.trim() || "";

    review
      .querySelectorAll(".jdgm-rev__pic-link, .jdgm-rev__product-picture")
      .forEach((link) => {
        if (link.hasAttribute("aria-label")) return;
        if (link.textContent.trim()) return;

        link.setAttribute(
          "aria-label",
          authorName
            ? `Customer review photo by ${authorName}`
            : "Customer review photo",
        );
      });
  }

  /**
   * Performs layout reads first and DOM writes afterward,
   * reducing forced synchronous reflows.
   */
  function initializeReviewReadMore() {
    const measurements = [];

    document.querySelectorAll(".jdgm-rev__body").forEach((body) => {
      if (body.dataset.readMoreChecked === "true") return;

      const paragraph = body.querySelector("p");
      if (!paragraph) {
        body.dataset.readMoreChecked = "true";
        return;
      }

      const computedStyle = window.getComputedStyle(paragraph);
      const lineHeight = Number.parseFloat(computedStyle.lineHeight);

      if (!Number.isFinite(lineHeight)) {
        body.dataset.readMoreChecked = "true";
        return;
      }

      measurements.push({
        body,
        paragraph,
        shouldClamp: paragraph.scrollHeight > lineHeight * 5 + 2,
      });
    });

    if (!measurements.length) return;

    requestAnimationFrame(() => {
      measurements.forEach(({ body, paragraph, shouldClamp }) => {
        body.dataset.readMoreChecked = "true";

        if (!shouldClamp) return;

        body.classList.add("is-clamped");

        const button = document.createElement("button");
        button.type = "button";
        button.className = "jdgm-review-toggle";
        button.textContent = "Read more";

        paragraph.insertAdjacentElement("afterend", button);
      });
    });
  }

  /**
   * Review form accessibility.
   */
  function enhanceReviewForm() {
    document.querySelectorAll(".jdgm-form__title").forEach((heading) => {
      heading.setAttribute("role", "heading");
      heading.setAttribute("aria-level", "2");
    });

    document.querySelectorAll(".jdgm-countdown").forEach((countdown) => {
      countdown.setAttribute("role", "status");
      countdown.setAttribute("aria-live", "polite");
      countdown.setAttribute("aria-atomic", "true");
    });

    document
      .querySelectorAll('textarea[name="review_body"]')
      .forEach((textarea, index) => {
        textarea.setAttribute(
          "aria-label",
          "Review Content, 5000 character limit",
        );

        const countdown = textarea
          .closest(".jdgm-form__fieldset")
          ?.querySelector(".jdgm-countdown");

        if (!countdown) return;

        if (!countdown.id) {
          countdown.id = `jdgm-countdown-${index + 1}`;
        }

        const describedBy = (
          textarea.getAttribute("aria-describedby") || ""
        )
          .split(/\s+/)
          .filter(Boolean);

        if (!describedBy.includes(countdown.id)) {
          describedBy.push(countdown.id);

          textarea.setAttribute(
            "aria-describedby",
            describedBy.join(" "),
          );
        }
      });
  }

  /**
   * Notification accessibility.
   */
  function enhanceNotifications() {
    document.querySelectorAll(".jdgm-notification").forEach((notification) => {
      notification.setAttribute("role", "status");
      notification.setAttribute("aria-live", "polite");
      notification.setAttribute("aria-atomic", "true");

      const title = notification.querySelector(
        ".jdgm-notification__title",
      );

      if (title) {
        title.setAttribute("role", "status");
        title.setAttribute("aria-live", "polite");
        title.setAttribute("aria-atomic", "true");
      }
    });
  }

  /**
   * Labels Judge.me medal modal triggers.
   */
  function labelJudgeMeModals() {
    document.querySelectorAll(".jdgm-modal").forEach((modal) => {
      if (!modal.hasAttribute("aria-label")) {
        modal.setAttribute("aria-label", "View Judge.me medals");
      }
    });
  }

  /**
   * Moves focus to the first newly loaded review.
   */
  function handleLoadedReviews() {
    if (!loadMoreTriggered) return;

    const reviews = getVisibleReviews();

    if (reviews.length <= reviewsBeforeLoad) return;

    const firstNewReview = reviews[reviewsBeforeLoad];
    if (!firstNewReview) return;

    loadMoreTriggered = false;
    firstNewReview.setAttribute("tabindex", "-1");

    requestAnimationFrame(() => {
      firstNewReview.focus({
        preventScroll: false,
      });
    });
  }

  /**
   * Runs all idempotent Judge.me enhancements.
   */
  function runJudgeMeEnhancements() {
    if (!document.querySelector('[class*="jdgm"]')) return;

    enhanceSummary();
    enhanceTabsRow();
    enhanceTabsAccessibility();

    document.querySelectorAll(".jdgm-rev").forEach(enhanceReview);

    enhanceReviewForm();
    enhanceNotifications();
    labelJudgeMeModals();
    initializeReviewReadMore();
    handleLoadedReviews();
  }

  /**
   * Read-more event delegation.
   */
  document.addEventListener("click", (event) => {
    const toggleButton = event.target.closest(".jdgm-review-toggle");

    if (toggleButton) {
      const body = toggleButton.closest(".jdgm-rev__body");
      if (!body) return;

      const isOpen = body.classList.toggle("is-open");

      toggleButton.textContent = isOpen ? "Show less" : "Read more";
      toggleButton.setAttribute("aria-expanded", isOpen ? "true" : "false");

      return;
    }

    const loadMoreButton = event.target.closest(
      ".jdgm-all-reviews-page__load-more",
    );

    if (!loadMoreButton) return;

    reviewsBeforeLoad = getVisibleReviews().length;
    loadMoreTriggered = true;
  });

  /**
   * Tracks keyboard activation of the load-more control.
   */
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;

    const loadMoreButton = event.target.closest(
      ".jdgm-all-reviews-page__load-more",
    );

    if (!loadMoreButton) return;

    reviewsBeforeLoad = getVisibleReviews().length;
    loadMoreTriggered = true;
  });

  /**
   * A single observer replaces all previous body observers.
   */
  const observer = new MutationObserver((mutations) => {
    const hasRelevantMutation = mutations.some((mutation) => {
      if (mutation.type === "attributes") {
        return (
          mutation.target instanceof HTMLElement &&
          mutation.target.closest('[class*="jdgm"]')
        );
      }

      return [...mutation.addedNodes].some((node) => {
        if (!(node instanceof HTMLElement)) return false;

        return (
          node.matches?.('[class*="jdgm"]') ||
          node.querySelector?.('[class*="jdgm"]')
        );
      });
    });

    if (hasRelevantMutation) {
      scheduleUpdate();
    }
  });

  function initialize() {
    runJudgeMeEnhancements();

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize, {
      once: true,
    });
  } else {
    initialize();
  }
})();